import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { TranslationEntry } from '../lib/api'
import { fetchTranslations as fetchTranslationEntries } from '../lib/api'
import { deferToIdle } from '../lib/idle'
import { LOCAL_TRANSLATIONS } from '../data/localTranslations'
import { getLastKnownCountry } from '../lib/analytics'

export type LanguageCode = 'en' | 'fr' | 'de' | 'es' | 'uk'

type LanguageContextValue = {
  language: LanguageCode
  setLanguage: (lang: LanguageCode) => void
  translate: (
    idOrDefault: string,
    defaultTextOrParams?: string | Record<string, string | number>,
    params?: Record<string, string | number>,
  ) => string
  translations: TranslationEntry[]
  loading: boolean
}

const LanguageContext = createContext<
  (LanguageContextValue & {
    translateWithParams?: (
      key: string,
      defaultText?: string,
      params?: Record<string, string | number>,
    ) => string
  }) | undefined
>(undefined)

const LANG_KEY = 'preferred_language'
const SUPPORTED_LANGUAGES: LanguageCode[] = ['en', 'fr', 'de', 'es', 'uk']
const DOM_SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT'])

const preserveWhitespace = (original: string, nextValue: string) => {
  if (!original) return nextValue
  const leading = original.length - original.trimStart().length
  const trailing = original.length - original.trimEnd().length
  const leadingWhitespace = leading > 0 ? original.slice(0, leading) : ''
  const trailingWhitespace = trailing > 0 ? original.slice(original.length - trailing) : ''
  return `${leadingWhitespace}${nextValue}${trailingWhitespace}`
}

const normalizeTextKey = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase()

const applyDomTranslations = (
  sourceMap: Record<string, TranslationEntry>,
  normalizedSourceMap: Record<string, TranslationEntry>,
  translatedLookup: Record<LanguageCode, Record<string, TranslationEntry>>,
  language: LanguageCode,
  originalStore: WeakMap<Node, string>,
) => {
  if (typeof document === 'undefined' || !document.body || typeof document.createTreeWalker !== 'function') {
    return
  }
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
  const nodesToUpdate: Array<{ node: Node; original: string; translated: string | null }> = []
  let current: Node | null = walker.nextNode()
  while (current) {
    const parent = current.parentElement
    if (parent) {
      if (parent.closest('[data-i18n-ignore]')) {
        current = walker.nextNode()
        continue
      }
      if (parent.isContentEditable) {
        current = walker.nextNode()
        continue
      }
      if (DOM_SKIP_TAGS.has(parent.tagName)) {
        current = walker.nextNode()
        continue
      }
    }
    const existingValue = originalStore.has(current) ? originalStore.get(current)! : current.nodeValue || ''
    if (!originalStore.has(current)) {
      let baseline = existingValue
      const lookupKey = normalizeTextKey(existingValue)
      const matchedEntry =
        sourceMap[existingValue.trim()] ||
        normalizedSourceMap[lookupKey] ||
        translatedLookup[language]?.[lookupKey]
      if (matchedEntry) {
        const sourceText = matchedEntry.source?.trim()
        baseline = sourceText || matchedEntry.translations?.en || baseline
      }
      originalStore.set(current, baseline)
    }
    const original = originalStore.get(current) || existingValue
    const trimmed = original.trim()
    if (!trimmed) {
      current = walker.nextNode()
      continue
    }
    const entry = sourceMap[trimmed] || normalizedSourceMap[normalizeTextKey(trimmed)]
    if (!entry) {
      nodesToUpdate.push({ node: current, original, translated: null })
      current = walker.nextNode()
      continue
    }
    const translated =
      entry.translations?.[language]?.trim() ||
      entry.translations?.en?.trim() ||
      null
    nodesToUpdate.push({
      node: current,
      original,
      translated,
    })
    current = walker.nextNode()
  }
  nodesToUpdate.forEach(({ node, original, translated }) => {
    if (!node.nodeValue) {
      return
    }
    if (!translated) {
      if (node.nodeValue !== original) {
        node.nodeValue = original
      }
      return
    }
    const nextValue = preserveWhitespace(original, translated)
    if (node.nodeValue !== nextValue) {
      node.nodeValue = nextValue
    }
  })
}

const isSupportedLanguage = (value: unknown): value is LanguageCode =>
  typeof value === 'string' && (SUPPORTED_LANGUAGES as string[]).includes(value)

const detectBrowserLanguage = (): LanguageCode => 'en'

const COUNTRY_LANGUAGE_MAP: Record<string, LanguageCode> = {
  UA: 'uk',
  ES: 'es',
  MX: 'es',
  AR: 'es',
  CO: 'es',
  CL: 'es',
  PE: 'es',
  FR: 'fr',
  BE: 'fr',
  CH: 'fr',
  DE: 'de',
  AT: 'de',
  // fallback handled by default language
}

type LanguageProviderProps = {
  children: React.ReactNode
  initialTranslations?: TranslationEntry[]
  fetchRemoteTranslations?: boolean
}

export function LanguageProvider({
  children,
  initialTranslations,
  fetchRemoteTranslations = false,
}: LanguageProviderProps) {
  const userPreferredRef = useRef(false)
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    if (typeof window === 'undefined') return 'en'
    const params = new URLSearchParams(window.location.search)
    const urlLang = params.get('lang')
    if (isSupportedLanguage(urlLang)) {
      userPreferredRef.current = true
      return urlLang
    }
    const stored = window.localStorage.getItem(LANG_KEY)
    if (isSupportedLanguage(stored)) {
      userPreferredRef.current = true
      return stored
    }
    return detectBrowserLanguage()
  })
  const bootstrapTranslations =
    (initialTranslations?.length ? initialTranslations : LOCAL_TRANSLATIONS) ?? []
  const [translations, setTranslations] = useState<TranslationEntry[]>(bootstrapTranslations)
  const [loading, setLoading] = useState(() => bootstrapTranslations.length === 0)
  const originalDomTextRef = useRef<WeakMap<Node, string>>(new WeakMap())
  const domRafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!fetchRemoteTranslations) {
      setLoading(false)
      return
    }
    let active = true
    const cancelIdle = deferToIdle(() => {
      fetchTranslationEntries()
        .then((data) => {
          if (!active) return
          setTranslations(data)
        })
        .catch((error) => {
          console.error('Failed to fetch translations', error)
        })
        .finally(() => {
          if (active) setLoading(false)
        })
    }, 1500)
    return () => {
      active = false
      cancelIdle()
    }
  }, [fetchRemoteTranslations])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANG_KEY, language)
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language
    }
  }, [language])

  useEffect(() => {
    const maybeSwitchByCountry = (country?: string | null) => {
      if (userPreferredRef.current) return
      if (!country) return
      const normalized = country.split('@')[0]?.trim().toUpperCase()
      const mapped = normalized ? COUNTRY_LANGUAGE_MAP[normalized] : undefined
      if (mapped && mapped !== language) {
        setLanguageState(mapped)
      }
    }
    if (typeof window !== 'undefined') {
      maybeSwitchByCountry(getLastKnownCountry())
      const handler = (event: Event) => {
        const detail = (event as CustomEvent<string | undefined>).detail || ''
        maybeSwitchByCountry(detail)
      }
      window.addEventListener('artist-country-update', handler as EventListener)
      return () => {
        window.removeEventListener('artist-country-update', handler as EventListener)
      }
    }
  }, [language])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.searchParams.set('lang', language)
    const next = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState({}, '', next)
  }, [language])

  const translationMaps = useMemo(() => {
    const byKey: Record<string, TranslationEntry> = {}
    const bySource: Record<string, TranslationEntry> = {}
    const bySourceNormalized: Record<string, TranslationEntry> = {}
    const byTranslatedNormalized: Record<LanguageCode, Record<string, TranslationEntry>> = {} as Record<
      LanguageCode,
      Record<string, TranslationEntry>
    >
    SUPPORTED_LANGUAGES.forEach((lang) => {
      byTranslatedNormalized[lang] = {}
    })
    translations.forEach((entry) => {
      const normalizedKey = typeof entry.key === 'string' ? entry.key.trim() : ''
      if (normalizedKey) byKey[normalizedKey] = entry
      const normalizedSource = typeof entry.source === 'string' ? entry.source.trim() : ''
      if (normalizedSource) {
        const normalizedKeyValue = normalizeTextKey(normalizedSource)
        bySource[normalizedSource] = entry
        bySourceNormalized[normalizedKeyValue] = entry
        byTranslatedNormalized.en[normalizedKeyValue] = entry
      }
      if (entry.translations) {
        Object.entries(entry.translations).forEach(([lang, value]) => {
          if (!isSupportedLanguage(lang) || typeof value !== 'string') return
          const trimmed = value.trim()
          if (!trimmed) return
          byTranslatedNormalized[lang][normalizeTextKey(trimmed)] = entry
        })
      }
    })
    return { byKey, bySource, bySourceNormalized, byTranslatedNormalized }
  }, [translations])

  useEffect(() => {
    if (loading) return
    if (typeof document === 'undefined' || !document.body) return
    const scheduleApply = () => {
      if (domRafRef.current !== null) {
        if (typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(domRafRef.current)
        } else {
          clearTimeout(domRafRef.current)
        }
        domRafRef.current = null
      }
      if (typeof requestAnimationFrame === 'function') {
        domRafRef.current = requestAnimationFrame(() => {
          applyDomTranslations(
            translationMaps.bySource,
            translationMaps.bySourceNormalized,
            translationMaps.byTranslatedNormalized,
            language,
            originalDomTextRef.current,
          )
          domRafRef.current = null
        })
      } else if (typeof window !== 'undefined') {
        domRafRef.current = window.setTimeout(() => {
          applyDomTranslations(
            translationMaps.bySource,
            translationMaps.bySourceNormalized,
            translationMaps.byTranslatedNormalized,
            language,
            originalDomTextRef.current,
          )
          domRafRef.current = null
        }, 16)
      } else {
        applyDomTranslations(
          translationMaps.bySource,
          translationMaps.bySourceNormalized,
          translationMaps.byTranslatedNormalized,
          language,
          originalDomTextRef.current,
        )
      }
    }
    scheduleApply()
    const observer = new MutationObserver(() => {
      scheduleApply()
    })
    observer.observe(document.body, { childList: true, subtree: true, characterData: true })
    return () => {
      observer.disconnect()
      if (domRafRef.current !== null) {
        if (typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(domRafRef.current)
        } else {
          clearTimeout(domRafRef.current)
        }
        domRafRef.current = null
      }
    }
  }, [translationMaps, language, loading])

  const replacePlaceholders = (
    input: string,
    params?: Record<string, string | number | undefined>,
  ) => {
    if (!params) return input
    return Object.entries(params).reduce((acc, [name, value]) => {
      const safeValue = value === undefined || value === null ? '' : String(value)
      const pattern = new RegExp(`{{\\s*${name}\\s*}}`, 'g')
      return acc.replace(pattern, safeValue)
    }, input)
  }

  const translate = useCallback(
    (
      idOrDefault: string,
      defaultTextOrParams?: string | Record<string, string | number>,
      maybeParams?: Record<string, string | number>,
    ) => {
      let defaultText: string | undefined
      let params: Record<string, string | number | undefined> | undefined
      if (
        defaultTextOrParams &&
        typeof defaultTextOrParams === 'object' &&
        !Array.isArray(defaultTextOrParams)
      ) {
        params = defaultTextOrParams
      } else {
        defaultText = defaultTextOrParams as string | undefined
        params = maybeParams
      }
      const keyCandidate = idOrDefault?.trim()
      const fallbackRaw =
        (defaultText ?? idOrDefault)?.toString() ??
        ''
      const fallbackLookup = fallbackRaw.trim()
      const normalizedSourceKey = fallbackLookup ? normalizeTextKey(fallbackLookup) : ''
      const entry =
        (keyCandidate && translationMaps.byKey[keyCandidate]) ||
        (fallbackLookup && translationMaps.bySource[fallbackLookup]) ||
        (normalizedSourceKey && translationMaps.bySourceNormalized[normalizedSourceKey])
      if (entry) {
        const translated =
          entry.translations?.[language]?.trim() ||
          entry.translations?.en?.trim() ||
          entry.source?.trim()
        if (translated) {
          return replacePlaceholders(translated, params)
        }
      }
      return replacePlaceholders(fallbackRaw, params)
    },
    [translationMaps, language],
  )

  const setLanguage = useCallback((lang: LanguageCode) => {
    userPreferredRef.current = true
    setLanguageState(isSupportedLanguage(lang) ? lang : 'en')
  }, [])

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      translate,
      translations,
      loading,
    }),
    [language, setLanguage, translate, translations, loading],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return { language: ctx.language, setLanguage: ctx.setLanguage, loading: ctx.loading }
}

export const useTranslation = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useTranslation must be used within a LanguageProvider')
  }
  return { t: ctx.translate, language: ctx.language, loading: ctx.loading }
}

export const LANGUAGES: Array<{ code: LanguageCode; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'uk', label: 'Українська' },
]
